"use client";

import { useState } from "react";
import { StandardButton } from "@/components/common/StandardButton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShippingProviderModal } from "@/components/modals";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";
import { Order, OrderStatus } from "@/types/models/order";
import {
  MoreHorizontal,
  Download,
  Check,
  X,
  Package,
  Truck,
  Eye,
  RotateCcw,
  Bell,
  RefreshCw,
  Ticket,
  Plus,
} from "lucide-react";

interface OrderActionsCellProps {
  order: Order;
}

interface ActionItem {
  label: string;
  action: () => void;
  variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "ghost"
    | "destructive"
    | "success";
  icon?: React.ReactNode;
}

const OrderActionsCell = ({ order }: OrderActionsCellProps) => {
  const dispatch = useAppDispatch();
  const { loadingById } = useAppSelector((state) => state.order);
  const isLoading = loadingById[order.id] || false;

  // Modal state
  const [showShippingModal, setShowShippingModal] = useState(false);

  const handleStatusUpdate = (newStatus: OrderStatus) => {
    // For now, we'll use approveOrder for positive changes and rejectOrder for cancellations
    if (newStatus === "CANCELLED") {
      dispatch(orderActions.rejectOrder(order.id));
    } else {
      dispatch(orderActions.approveOrder(order.id));
    }
  };

  const handleDispatch = () => {
    setShowShippingModal(true);
  };

  const handleSelectProvider = (provider: any) => {
    console.log("Selected shipping provider:", provider);
    // Here you can dispatch an action to update the order with the selected provider
    // For now, we'll just update the status to SHIPPING
    handleStatusUpdate("SHIPPING");
  };

  const handleCancelOrder = () => {
    dispatch(orderActions.rejectOrder(order.id));
  };

  const handleDownloadLabel = () => {
    // Implementation for downloading shipping label
    console.log("Downloading label for order:", order.order_number || order.id);
  };

  const handleReady = () => {
    handleStatusUpdate("SHIPPING");
  };

  const handlePickedUp = () => {
    handleStatusUpdate("SHIPPING");
  };

  const handleAddUpdate = () => {
    // Implementation for adding status update
    console.log("Adding update for order:", order.order_number || order.id);
  };

  const handleInspect = () => {
    // Implementation for inspecting order
    console.log("Inspecting order:", order.order_number || order.id);
  };

  const handleReturn = () => {
    handleStatusUpdate("RETURNING");
  };

  const handleNotify = () => {
    // Implementation for notifying customer
    console.log(
      "Notifying customer for order:",
      order.order_number || order.id,
    );
  };

  const handleReProcess = () => {
    handleStatusUpdate("CREATED");
  };

  const handleCreateTicket = () => {
    // Implementation for creating support ticket
    console.log("Creating ticket for order:", order.order_number || order.id);
  };

  const getAvailableActions = (): ActionItem[] => {
    const actions: ActionItem[] = [];

    switch (order.current_order_stage) {
      case "CREATED":
        actions.push(
          {
            label: "Confirm & Dispatch",
            action: handleDispatch,
            variant: "primary",
            icon: <Truck className="w-4 h-4" />,
          },
          {
            label: "Cancel",
            action: handleCancelOrder,
            variant: "destructive",
            icon: <X className="w-4 h-4" />,
          },
        );
        break;
      case "CONFIRMED":
        actions.push(
          {
            label: "Download Label",
            action: handleDownloadLabel,
            variant: "secondary",
            icon: <Download className="w-4 h-4" />,
          },
          {
            label: "Ship",
            action: handleReady,
            variant: "success",
            icon: <Check className="w-4 h-4" />,
          },
          {
            label: "Cancel",
            action: handleCancelOrder,
            variant: "destructive",
            icon: <X className="w-4 h-4" />,
          },
        );
        break;
      case "SHIPPING":
        actions.push(
          {
            label: "Track",
            action: handlePickedUp,
            variant: "primary",
            icon: <Package className="w-4 h-4" />,
          },
          {
            label: "Add Update",
            action: handleAddUpdate,
            variant: "secondary",
            icon: <Plus className="w-4 h-4" />,
          },
        );
        break;
      case "RETURNING":
        actions.push(
          {
            label: "Add Update",
            action: handleAddUpdate,
            variant: "secondary",
            icon: <Plus className="w-4 h-4" />,
          },
          {
            label: "Create Ticket",
            action: handleCreateTicket,
            variant: "outline",
            icon: <Ticket className="w-4 h-4" />,
          },
        );
        break;
      case "COMPLETED":
        actions.push(
          {
            label: "Return",
            action: handleReturn,
            variant: "secondary",
            icon: <RotateCcw className="w-4 h-4" />,
          },
          {
            label: "Notify",
            action: handleNotify,
            variant: "outline",
            icon: <Bell className="w-4 h-4" />,
          },
        );
        break;
      case "CANCELLED":
        actions.push({
          label: "Re-Process",
          action: handleReProcess,
          variant: "primary",
          icon: <RefreshCw className="w-4 h-4" />,
        });
        break;
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  if (availableActions.length === 0) {
    return <span className="text-gray-400">No actions</span>;
  }

  if (availableActions.length === 1) {
    const action = availableActions[0];
    return (
      <>
        <StandardButton
          variant={action.variant || "primary"}
          size="sm"
          onClick={action.action}
          disabled={isLoading}
          className="h-8 px-3 gap-2"
        >
          {action.icon}
          {isLoading ? "Loading..." : action.label}
        </StandardButton>

        {/* Shipping Provider Modal */}
        <ShippingProviderModal
          isOpen={showShippingModal}
          onClose={() => setShowShippingModal(false)}
          onSelectProvider={handleSelectProvider}
        />
      </>
    );
  }

  // For multiple actions, show buttons inline in a row
  if (availableActions.length <= 5) {
    return (
      <>
        <div className="flex gap-1 items-center">
          {availableActions.map((action, index) => (
            <StandardButton
              key={index}
              variant={action.variant || "primary"}
              size="sm"
              onClick={action.action}
              disabled={isLoading}
              className="h-8 px-2 text-xs gap-1 flex-shrink-0"
            >
              {action.icon}
              {isLoading && index === 0 ? "Loading..." : action.label}
            </StandardButton>
          ))}
        </div>

        {/* Shipping Provider Modal */}
        <ShippingProviderModal
          isOpen={showShippingModal}
          onClose={() => setShowShippingModal(false)}
          onSelectProvider={handleSelectProvider}
        />
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0" disabled={isLoading}>
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {availableActions.map((action, index) => (
          <DropdownMenuItem
            key={index}
            onClick={action.action}
            className={`flex items-center gap-2 ${
              action.variant === "destructive"
                ? "text-red-600 focus:text-red-600"
                : ""
            }`}
          >
            {action.icon}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>

      {/* Shipping Provider Modal */}
      <ShippingProviderModal
        isOpen={showShippingModal}
        onClose={() => setShowShippingModal(false)}
        onSelectProvider={handleSelectProvider}
      />
    </DropdownMenu>
  );
};

export default OrderActionsCell;
